/**
 * Utilidades para RouterOS
 * Funciones helper para generación de scripts MikroTik
 */

/**
 * Genera comando de set interface según versión de RouterOS
 * @param {string} version - 'v6' o 'v7'
 * @param {string} defaultName - Nombre por defecto (ej: 'ether1')
 * @param {string} newName - Nuevo nombre (ej: 'WAN1')
 * @returns {string} Comando completo
 */
export function getInterfaceSetCommand(version, defaultName, newName) {
  if (version === "v7") {
    return `set [find default-name=${defaultName}] name=${newName}`;
  } else {  // v6
    return `set ${defaultName} name=${newName}`;
  }
}

/**
 * Obtiene el separador de sección según versión
 * @param {string} version - 'v6' o 'v7'
 * @returns {string} Separador
 */
export function getSectionSeparator(version) {
  return version === "v7" ? "\n" : "\n";
}

/**
 * Extrae la IP base de una subred CIDR
 * Ejemplo: "192.168.1.0/24" → "192.168.1"
 * @param {string} cidr - Subred en formato CIDR
 * @returns {string} IP base
 */
export function getBaseIp(cidr) {
  const network = cidr.split('/')[0];
  return network.split('.').slice(0, 3).join('.');
}

/**
 * Extrae la red completa de una subred CIDR
 * Ejemplo: "192.168.1.0/24" → "192.168.1.0"
 * @param {string} cidr - Subred en formato CIDR
 * @returns {string} Red completa
 */
export function getNetwork(cidr) {
  return cidr.split('/')[0];
}

/**
 * Genera IP del gateway LAN
 * Ejemplo: "192.168.1.0/24" → "192.168.1.1"
 * @param {string} cidr - Subred en formato CIDR
 * @returns {string} IP del gateway
 */
export function getLanGatewayIp(cidr) {
  const baseIp = getBaseIp(cidr);
  return `${baseIp}.1`;
}

/**
 * Genera rango DHCP
 * @param {string} cidr - Subred en formato CIDR
 * @param {number} start - Inicio del rango (default: 2)
 * @param {number} end - Fin del rango (default: 254)
 * @returns {object} { start: string, end: string }
 */
export function getDhcpRange(cidr, start = 2, end = 254) {
  const baseIp = getBaseIp(cidr);
  return {
    start: `${baseIp}.${start}`,
    end: `${baseIp}.${end}`
  };
}

/**
 * Genera letra para routing mark (A, B, C, etc.)
 * @param {number} index - Índice (1, 2, 3, ...)
 * @returns {string} Letra correspondiente
 */
export function getRoutingMarkLetter(index) {
  return String.fromCharCode(64 + index);  // A=65, B=66, etc.
}

/**
 * Formatea fecha actual para scripts
 * @returns {string} Fecha formateada
 */
export function getFormattedDate() {
  const now = new Date();
  return now.toLocaleString('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

/**
 * Genera un ID único
 * @returns {string} ID único
 */
export function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
}

/**
 * Calcula netmask desde cantidad de bits
 * Ejemplo: 24 → "255.255.255.0"
 * @param {number} bits - Cantidad de bits
 * @returns {string} Netmask
 */
export function bitsToNetmask(bits) {
  const masks = {
    8: '255.0.0.0',
    16: '255.255.0.0',
    24: '255.255.255.0',
    32: '255.255.255.255'
  };
  return masks[bits] || '255.255.255.0';
}

/**
 * Cuenta bits de netmask
 * Ejemplo: "255.255.255.0" → 24
 * @param {string} netmask - Netmask
 * @returns {number} Bits
 */
export function netmaskToBits(netmask) {
  const parts = netmask.split('.');
  let bits = 0;
  parts.forEach(part => {
    const num = parseInt(part);
    bits += num.toString(2).split('1').length - 1;
  });
  return bits;
}

/**
 * Valida formato de IP
 * @param {string} ip - IP a validar
 * @returns {boolean} true si es válida
 */
export function isValidIp(ip) {
  const pattern = /^(\d{1,3}\.){3}\d{1,3}$/;
  if (!pattern.test(ip)) return false;
  
  const parts = ip.split('.');
  return parts.every(part => {
    const num = parseInt(part);
    return num >= 0 && num <= 255;
  });
}

/**
 * Genera comentario de header para scripts
 * @param {string} title - Título del script
 * @param {object} config - Configuración
 * @returns {string} Header formateado
 */
export function generateScriptHeader(title, config) {
  const version = config.routerosVersion || 'v7';
  return `# ${title}
# Generated: ${getFormattedDate()}
# RouterOS Version: ${version.toUpperCase()}
# Generated by: MikroTik Builder v2.0.0 (Offline)

`;
}
